import {
  REDACTED_IMAGE,
  REDACTED_TEXT,
  ROLE_ENDUSER,
} from '@app/constants/general';
import { CHANNEL } from '@app/constants/message';
import { MESSAGE_SOURCE } from '@app/constants/messages';
import { formatUserDisplayName } from '@app/helpers/format';

export interface ManualTextRedactionCandidate {
  isInbound?: boolean;
  isEvent?: boolean;
  isAI?: boolean;
  isFile?: boolean;
  isImage?: boolean;
  isNote?: boolean;
  fileUrl?: string | null;
  type?: string;
  channel?: string;
  source?: string;
  body?: string;
  messageBody?: string;
  author?: { role?: string };
  additionalInfo?: { recordingUrl?: string; eventType?: string };
  redactionData?: { Type?: string }[];
}

const hasRedactionType = (
  message: { redactionData?: { Type?: string }[] },
  type: string,
): boolean => (message?.redactionData ?? []).some(({ Type }) => Type === type);

export const isManuallyRedactedMessage = (
  message: ManualTextRedactionCandidate,
): boolean => hasRedactionType(message, REDACTED_TEXT);

export const isImageRedactedMessage = (message: {
  redactionData?: { Type?: string }[];
}): boolean => hasRedactionType(message, REDACTED_IMAGE);

const isVoiceTranscription = (message: ManualTextRedactionCandidate) =>
  message.additionalInfo?.eventType === 'voice' ||
  Boolean(message.additionalInfo?.recordingUrl);

const isEndUserAuthored = (message: ManualTextRedactionCandidate) =>
  message.author?.role === ROLE_ENDUSER &&
  (!message.source || message.source === MESSAGE_SOURCE.CUSTOMER);

const hasVisibleText = (message: ManualTextRedactionCandidate) => {
  const text = message.messageBody ?? message.body;
  return typeof text === 'string' && text.trim().length > 0;
};

export const isManualTextRedactable = (
  message: ManualTextRedactionCandidate,
): boolean =>
  Boolean(message) &&
  message.isInbound === true &&
  isEndUserAuthored(message) &&
  !message.isNote &&
  !message.isEvent &&
  !message.isAI &&
  !message.isImage &&
  !message.isFile &&
  !message.fileUrl &&
  message.type !== 'media' &&
  message.channel !== CHANNEL.VOICE &&
  !isVoiceTranscription(message) &&
  hasVisibleText(message) &&
  !isManuallyRedactedMessage(message);

export const handleImageRedaction = (
  message: { _id: string; id: string },
  redactionData: string,
) => {
  const redactedText = `Redacted Image - ${message?._id || message?.id}`;
  return {
    ...message,
    isImageRedacted: true,
    body: redactedText,
    redactedBody: redactedText,
    redactionData,
  };
};

export const updateRedactionViewedEvents = (
  currentEvents: unknown[],
  authUser: { _id: string; firstName?: string; lastName?: string },
) => {
  const newRedactionViewEvent = {
    userId: authUser?._id,
    operatorName: formatUserDisplayName(authUser),
    eventTime: new Date().getTime(),
  };
  return [...currentEvents, newRedactionViewEvent];
};
